// src/pages/Home.jsx
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import '../styles/home.css';
import { Helmet } from 'react-helmet-async';
import PublicHeader from '../components/common/PublicHeader';

// Lazy load footer and animations for mobile performance
const PublicFooter = lazy(() => import('../components/common/PublicFooter'));

// Conditionally import heavy animation script
const initHomeAnimations = () => {
  if (typeof window !== 'undefined' && window.innerWidth > 768) {
    import('../scripts/homeAnimations').then(module => {
      module.initHomeAnimations();
    });
  }
};

const Home = () => {
  const { user } = useAuth();
  const [isVisible, setIsVisible] = useState({
    features: false,
    testimonials: false,
    howItWorks: false
  });
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  const featuresRef = useRef(null);
  const testimonialsRef = useRef(null);
  const howItWorksRef = useRef(null);

  // Detect mobile device on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to conditionally apply animations
  const getAnimationClasses = (baseClasses, animationClasses) => {
    return isMobile ? baseClasses : `${baseClasses} ${animationClasses}`;
  };

  // Handle scroll events for animations and progress bar
  useEffect(() => {
    const handleScroll = () => {
      // Skip expensive calculations on mobile for better performance
      const isMobile = window.innerWidth <= 768;
      
      if (!isMobile) {
        // Calculate scroll progress for the progress bar (desktop only)
        const totalHeight = document.body.scrollHeight - window.innerHeight;
        const progress = (window.scrollY / totalHeight) * 100;
        setScrollProgress(progress);
      }
      
      // Simplified viewport detection for mobile
      const isInViewport = (element) => {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.top <= window.innerHeight * (isMobile ? 0.9 : 0.75);
      };
      
      setIsVisible({
        features: isInViewport(featuresRef.current),
        testimonials: isInViewport(testimonialsRef.current),
        howItWorks: isInViewport(howItWorksRef.current)
      });
      
      // Hide scroll indicator on scroll
      const scrollIndicator = document.getElementById('scrollIndicator');
      if (scrollIndicator && window.scrollY > 50) {
        scrollIndicator.classList.add('fade-out');
      }
    };
    
    // Throttle scroll events for better mobile performance
    let timeoutId = null;
    const throttledScroll = () => {
      if (timeoutId === null) {
        timeoutId = setTimeout(() => {
          handleScroll();
          timeoutId = null;
        }, window.innerWidth <= 768 ? 100 : 16); // Slower on mobile
      }
    };
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    handleScroll(); // Check initial state
    
    // Auto-rotate testimonials (slower on mobile to save battery)
    const testimonialInterval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % 3);
    }, window.innerWidth <= 768 ? 7000 : 5000);
    
    // Conditionally initialize animations (lighter on mobile)
    if (window.innerWidth > 768) {
      initHomeAnimations();
    }
    
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      clearInterval(testimonialInterval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>CategoriX - AI-Powered Product Categorization &amp; Taxonomy Mapping</title>
        <meta name="description" content="Automate product categorization with AI. Map your products to Amazon, Google, eBay, Shopify taxonomies in minutes. 99% accuracy, save days of manual work. Free trial." />
        <meta name="keywords" content="product categorization, taxonomy mapping, AI categorization, Amazon taxonomy, Google shopping taxonomy, product classification, ecommerce automation" />
        
        {/* Open Graph tags for social media */}
        <meta property="og:title" content="CategoriX - AI-Powered Product Categorization &amp; Taxonomy Mapping" />
        <meta property="og:description" content="Automate product categorization with AI. Map your products to Amazon, Google, eBay, Shopify taxonomies in minutes. 99% accuracy, save days of manual work." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://categorix.ai" />
        <meta property="og:image" content="https://categorix.ai/images/og-image-home.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="CategoriX" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CategoriX - AI-Powered Product Categorization" />
        <meta name="twitter:description" content="Automate product categorization with AI. 99% accuracy, save days of manual work. Free trial available." />
        <meta name="twitter:image" content="https://categorix.ai/images/twitter-card-home.jpg" />
        
        {/* Additional SEO tags */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <link rel="canonical" href="https://categorix.ai" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "CategoriX",
            "url": "https://categorix.ai",
            "logo": "https://categorix.ai/images/categorix-logo.png",
            "description": "AI-powered product categorization and taxonomy mapping platform for ecommerce businesses",
            "sameAs": [
              "https://twitter.com/categorix_ai",
              "https://linkedin.com/company/categorix"
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "US"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+1-555-CATEGORIX",
              "contactType": "customer service",
              "availableLanguage": "English"
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CategoriX",
            "applicationCategory": "BusinessApplication",
            "description": "AI-powered product categorization and taxonomy mapping platform",
            "offers": {
              "@type": "Offer",
              "priceCurrency": "USD",
              "price": "0",
              "priceValidUntil": "2025-12-31",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "150"
            }
          })}
        </script>
      </Helmet>
      
      <PublicHeader transparent={true} isHomePage={true} />
      
      <div className="bg-white relative overflow-x-hidden">
        {/* Scroll Progress Bar - Desktop only for performance */}
        {typeof window !== 'undefined' && window.innerWidth > 768 && (
          <div className="fixed top-0 left-0 right-0 h-1 z-50 bg-gray-200">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300 ease-out"
              style={{ width: `${scrollProgress}%` }}
            />
          </div>
        )}
        
        {/* Hero section */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-gray-100" />
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="relative shadow-xl sm:rounded-2xl sm:overflow-hidden">
              <div className="absolute inset-0">
                {/* Hero background image - optimized for LCP with responsive loading */}
                <picture>
                  {/* Mobile-optimized image (smaller file) */}
                  <source 
                    media="(max-width: 768px)" 
                    srcSet="/images/home/hero-background-mobile.jpg"
                    type="image/jpeg" 
                  />
                  {/* Desktop image */}
                  <img
                    src="/images/home/hero-background.jpg"
                    alt="AI-Powered Product Categorization with Categorix"
                    className="h-full w-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                    width="1920"
                    height="1080"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'block';
                    }}
                  />
                </picture>
                {/* Fallback gradient background if image fails to load - no animation/fade */}
                <div 
                  className="absolute inset-0 bg-gradient-to-br from-indigo-800 via-blue-700 to-indigo-700" 
                  style={{display: 'none'}}
                />
                {/* Dark overlay for better text contrast - no animation/fade */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-800/70 via-blue-700/70 to-indigo-700/70 mix-blend-multiply" />
                {/* Animated background elements - no opacity-0/animation-fade classes */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
                  <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
                  <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
                </div>
                {/* Add decorative elements - no opacity-0/animation-fade classes */}
                <div className="absolute inset-0 opacity-10">
                  <svg className="absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/4" width="404" height="404" fill="none" viewBox="0 0 404 404">
                    <defs>
                      <pattern id="85737c0e-0916-41d7-917f-596dc7edfa27" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                        <rect x="0" y="0" width="4" height="4" className="text-white" fill="currentColor" />
                      </pattern>
                    </defs>
                    <rect width="404" height="404" fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)" />
                  </svg>
                  <svg className="absolute bottom-0 left-0 transform -translate-x-1/3 translate-y-1/4" width="404" height="404" fill="none" viewBox="0 0 404 404">
                    <defs>
                      <pattern id="85737c0e-0916-41d7-917f-596dc7edfa28" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                        <rect x="0" y="0" width="4" height="4" className="text-white" fill="currentColor" />
                      </pattern>
                    </defs>
                    <rect width="404" height="404" fill="url(#85737c0e-0916-41d7-917f-596dc7edfa28)" />
                  </svg>
                </div>
              </div>
              
              <div className="relative px-4 py-16 sm:px-6 sm:py-24 lg:py-32 lg:px-8">
                {/* Logo Banner */}
                <div className={getAnimationClasses(
                  "flex justify-center mb-16", 
                  "opacity-0 animate-fade-in-up"
                )}>
                  <img 
                    src="/images/categorix-logo-white.png" 
                    alt="CategoriX" 
                    className="h-16 sm:h-20 md:h-24 w-auto"
                    loading="eager"
                    width="200"
                    height="96"
                  />
                </div>
                
                <h1 className="text-center text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                  <span className={getAnimationClasses(
                    "block text-white", 
                    "opacity-0 animate-fade-in-up animation-delay-300"
                  )}>AI-Powered Product Categorization</span>
                  <span className={getAnimationClasses(
                    "block text-blue-200", 
                    "opacity-0 animate-fade-in-up animation-delay-600"
                  )}>For All Ecommerce Platforms</span>
                </h1>
                <p className={getAnimationClasses(
                  "mt-6 max-w-lg mx-auto text-center text-xl text-blue-100 sm:max-w-3xl", 
                  "opacity-0 animate-fade-in-up animation-delay-900"
                )}>
                Perfectly categorize your entire product catalog in minutes. Our system matches your products to Amazon, Google, eBay, Shopify taxonomies - or your own custom categories. Eliminate tedious work, reduce errors, and get back to growing your business.
                </p>
                
                {/* Stats counters */}
                <div className={getAnimationClasses(
                  "mt-10 max-w-3xl mx-auto grid grid-cols-3 gap-8 text-center", 
                  "opacity-0 animate-fade-in-up animation-delay-1200"
                )}>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-white text-2xl xs:text-4xl font-bold">
                      <span className="counter" data-target="99">99</span>%
                    </div>
                    <div className="text-blue-100 text-xs sm:text-sm mt-1">Accuracy Rate</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-white text-2xl xs:text-4xl font-bold">
                      <span className="counter" data-target="500">500</span>+
                    </div>
                    <div className="text-blue-100 text-xs sm:text-sm mt-1">Products/Min</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-white text-2xl xs:text-4xl font-bold">
                      Days
                    </div>
                    <div className="text-blue-100 text-xs sm:text-sm mt-1">Saved Weekly</div>
                  </div>
                </div>
                
                <div className={getAnimationClasses(
                  "mt-10 max-w-sm mx-auto sm:max-w-none sm:flex sm:justify-center", 
                  "opacity-0 animate-fade-in-up animation-delay-1500"
                )}>
                  <div className="space-y-4 sm:space-y-0 sm:mx-auto sm:inline-grid sm:grid-cols-2 sm:gap-5">
                    {user ? (
                      <Link
                        to="/dashboard"
                        className="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-md text-indigo-700 bg-white hover:bg-blue-50 transition-all duration-300 sm:px-8 hover:scale-105"
                      >
                        Go to Dashboard
                      </Link>
                    ) : (
                      <Link
                        to="/register"
                        className="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-md text-indigo-700 bg-white hover:bg-blue-50 transition-all duration-300 sm:px-8 hover:scale-105"
                      >
                        Get Started Free
                      </Link>
                    )}
                    <Link
                      to="/pricing"
                      className="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-md text-white bg-blue-500 bg-opacity-70 hover:bg-opacity-80 transition-all duration-300 sm:px-8 hover:scale-105"
                    >
                      View Pricing
                    </Link>
                  </div>
                </div>
                
                {/* Login link for existing users - with proper animation */}
                {!user && (
                  <div className="mt-4 text-center">
                    <Link to="/login" className="login-link text-blue-200 hover:text-white text-sm font-medium transition-colors duration-300 underline hover:no-underline">
                      Already have an account? Log in
                    </Link>
                  </div>
                )}
                
                {/* Mouse scroll indicator with reduced spacing */}
                <div id="scrollIndicator" className={getAnimationClasses(
                  "relative mt-12 flex flex-col items-center pointer-events-none", 
                  "opacity-0 animate-fade-in-up animation-delay-1500"
                )}>
                  <span className="text-blue-200 text-sm mb-1">Scroll to explore</span>
                  <div className="w-6 h-10 border-2 border-blue-200 rounded-full flex justify-center pt-1">
                    <div className="w-1 h-2 bg-blue-200 rounded-full animate-scroll-down"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What is Taxonomy Mapping section */}
        <div className="py-16 bg-white overflow-hidden" ref={howItWorksRef}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12">
              <div className={`transform transition-all duration-1000 ease-in-out ${isVisible.howItWorks ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
                <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">Why Product Categorization Matters</h2>
                <p className="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
                  Save hours manually spent sorting products
                </p>
                <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                  Manually matching your products to marketplace categories is tedious, error-prone, and time-consuming. Categorix automates this process, instantly mapping your products to standardized category systems used by Amazon, Google, eBay, Walmart and others. You can also match products to your own custom category structure.
                </p>
                <div className="mt-6">
                  <p className="text-lg text-gray-700 font-medium">
                    Without proper product categorization, your products may be:
                  </p>
                  <ul className="mt-4 space-y-4">
                    <li className="flex items-start transform transition-all hover:translate-x-1 hover:text-indigo-600 duration-300">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="ml-3 text-base text-gray-600 group-hover:text-indigo-600">
                        <strong className="text-gray-800">Invisible to shoppers</strong> – Products won't appear in relevant searches, reducing visibility by up to 70%
                      </p>
                    </li>
                    <li className="flex items-start transform transition-all hover:translate-x-1 hover:text-indigo-600 duration-300">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="ml-3 text-base text-gray-600 group-hover:text-indigo-600">
                        <strong className="text-gray-800">Rejected by marketplaces</strong> – Wrong categories lead to listing rejections, causing costly delays and lost sales
                      </p>
                    </li>
                    <li className="flex items-start transform transition-all hover:translate-x-1 hover:text-indigo-600 duration-300">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="ml-3 text-base text-gray-600 group-hover:text-indigo-600">
                        <strong className="text-gray-800">Hours wasted on manual work</strong> – Manually categorizing thousands of products steals valuable time from your team
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="mt-6">
                  <Link to="/blog/taxonomy-guide" className="text-indigo-600 hover:text-indigo-800 font-medium">
                    Learn more about taxonomy mapping →
                  </Link>
                </div>
              </div>
              <div className={`mt-12 relative text-base mx-auto lg:mt-0 transform transition-all duration-1000 ease-in-out ${isVisible.howItWorks ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
                <div className="aspect-w-5 aspect-h-4 lg:aspect-none">
                  <div className="rounded-xl shadow-xl overflow-hidden bg-gradient-to-br from-indigo-50 to-blue-50 h-full">
                    <div className="h-full p-8 flex flex-col justify-center">
                      <div className="mb-8 flex justify-center">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600 flex items-center justify-center text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">How Categorix Works</h3>
                      <ol className="space-y-6 text-gray-600">
                        <li className="flex items-center transform transition-all hover:translate-x-1 duration-300">
                          <span className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-600 font-medium">1</span>
                          <p className="ml-4 text-base">Upload product data (CSV, Excel, JSON, or text)</p>
                        </li>
                        <li className="flex items-center transform transition-all hover:translate-x-1 duration-300">
                          <span className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-600 font-medium">2</span>
                          <p className="ml-4 text-base">Select attributes AI uses for classification (titles, descriptions)</p>
                        </li>
                        <li className="flex items-center transform transition-all hover:translate-x-1 duration-300">
                          <span className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-600 font-medium">3</span>
                          <p className="ml-4 text-base">Choose target taxonomy (Amazon, Shopify, or your custom one)</p>
                        </li>
                        <li className="flex items-center transform transition-all hover:translate-x-1 duration-300">
                          <span className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-600 font-medium">4</span>
                          <p className="ml-4 text-base">AI maps products to correct categories in minutes</p>
                        </li>
                        <li className="flex items-center transform transition-all hover:translate-x-1 duration-300">
                          <span className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-600 font-medium">5</span>
                          <p className="ml-4 text-base">Download in your preferred format or template</p>
                        </li>
                      </ol>
                      
                      {/* Simple SVG diagram */}
                      <div className="mt-8 flex justify-center">
                        <div className="w-full max-w-xs text-center">
                          <div className="flex items-center justify-between">
                            <div className="bg-indigo-100 rounded-lg p-2 w-20">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                              </svg>
                              <span className="text-xs text-gray-600 mt-1 block">Upload</span>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <div className="bg-indigo-100 rounded-lg p-2 w-20">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                              <span className="text-xs text-gray-600 mt-1 block">AI Maps</span>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <div className="bg-indigo-100 rounded-lg p-2 w-20">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              <span className="text-xs text-gray-600 mt-1 block">Download</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Features section */}
        <div className="py-16 bg-gradient-to-b from-gray-50 to-white" ref={featuresRef}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase opacity-0 animate-fade-in-up">Key Capabilities</h2>
              <p className={`mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl transform transition-all duration-1000 ease-in-out ${isVisible.features ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                How Categorix Transforms Your Workflow
              </p>
              <p className={`max-w-xl mt-5 mx-auto text-xl text-gray-500 leading-relaxed transform transition-all duration-1000 ease-in-out delay-300 ${isVisible.features ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                Replace tedious manual categorization with an AI solution that saves time, increases accuracy, and scales effortlessly across multiple marketplaces.
              </p>
            </div>

            <div className="mt-12">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {/* Card 1 */}
                <div className={`pt-6 transform transition-all duration-700 ease-in-out ${isVisible.features ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '150ms' }}>
                  <div className="flow-root bg-white rounded-lg shadow-lg px-6 pb-8 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-md shadow-lg">
                          <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Save Days Every Week</h3>
                      <p className="mt-5 text-base text-gray-500">
                        Process 500+ products per minute instead of manually categorizing 50-100 products per hour. What used to take days now happens in minutes with 99% accuracy.
                      </p>
                      <p className="mt-3 text-base text-gray-500">
                        Reclaim entire days weekly that your team previously spent on tedious manual categorization tasks.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Card 2 */}
                <div className={`pt-6 transform transition-all duration-700 ease-in-out ${isVisible.features ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '300ms' }}>
                  <div className="flow-root bg-white rounded-lg shadow-lg px-6 pb-8 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-md shadow-lg">
                          <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Connect to Any Marketplace</h3>
                      <p className="mt-5 text-base text-gray-500">
                        Instantly map to Amazon, Google, eBay, Walmart, Shopify and more. Our system stays updated with the latest marketplace taxonomy changes.
                      </p>
                      <p className="mt-3 text-base text-gray-500">
                        Create and use your own custom taxonomies to maintain consistent categorization across all your sales channels.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Card 3 */}
                <div className={`pt-6 transform transition-all duration-700 ease-in-out ${isVisible.features ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '450ms' }}>
                  <div className="flow-root bg-white rounded-lg shadow-lg px-6 pb-8 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-md shadow-lg">
                          <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Flexible Data Integration</h3>
                      <p className="mt-5 text-base text-gray-500">
                        Import from any source—CSV, Excel, JSON, or text. Select which product attributes to use (titles, descriptions, existing categories).
                      </p>
                      <p className="mt-3 text-base text-gray-500">
                        Export mapped categories in your preferred format or template for immediate use in any platform or your own system.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Card 4 */}
                <div className={`pt-6 transform transition-all duration-700 ease-in-out ${isVisible.features ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '600ms' }}>
                  <div className="flow-root bg-white rounded-lg shadow-lg px-6 pb-8 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-md shadow-lg">
                          <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Self-Improving AI</h3>
                      <p className="mt-5 text-base text-gray-500">
                        Our AI gets smarter with every use. It learns from any manual adjustments you make to improve accuracy on future categorizations.
                      </p>
                      <p className="mt-3 text-base text-gray-500">
                        The more you use it, the better it maps your specific product catalog—maintaining consistent 99% accuracy for your unique inventory.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Card 5 */}
                <div className={`pt-6 transform transition-all duration-700 ease-in-out ${isVisible.features ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '750ms' }}>
                  <div className="flow-root bg-white rounded-lg shadow-lg px-6 pb-8 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-md shadow-lg">
                          <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Detailed Analytics & Insights</h3>
                      <p className="mt-5 text-base text-gray-500">
                        Track mapping accuracy, processing time, and confidence scores. Identify patterns to improve your product data quality.
                      </p>
                      <p className="mt-3 text-base text-gray-500">
                        Get actionable recommendations for product enrichment to boost marketplace performance and conversion rates.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Card 6 */}
                <div className={`pt-6 transform transition-all duration-700 ease-in-out ${isVisible.features ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '900ms' }}>
                  <div className="flow-root bg-white rounded-lg shadow-lg px-6 pb-8 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-md shadow-lg">
                          <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Seamless Integration</h3>
                      <p className="mt-5 text-base text-gray-500">
                        Integrate with your PIM system, ERP, spreadsheets, or e-commerce platform. Our API enables automated workflows and real-time classification.
                      </p>
                      <p className="mt-3 text-base text-gray-500">
                        Scale your catalog across multiple marketplaces efficiently, maximizing ROI without increasing staffing costs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials section */}
        <div className="bg-white py-16 relative overflow-hidden" ref={testimonialsRef}>
          {/* Background decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-indigo-300 rounded-full"></div>
            <div className="absolute bottom-10 right-10 w-72 h-72 bg-blue-300 rounded-full"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">Results That Speak</h2>
              <p className={`mt-1 text-3xl font-extrabold text-gray-900 sm:text-4xl transform transition-all duration-1000 ease-in-out ${isVisible.testimonials ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                Businesses saving hours every week
              </p>
            </div>
            
            {/* Testimonial Carousel */}
            <div className={`mt-12 transform transition-all duration-1000 ease-in-out ${isVisible.testimonials ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <div className="relative h-auto max-w-3xl mx-auto">
                {/* Testimonial 1 */}
                <div className={`transform transition-all duration-500 ease-in-out ${activeTestimonial === 0 ? 'opacity-100 z-10' : 'opacity-0 absolute inset-0 -z-10'}`}>
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 shadow-xl">
                    <div className="flex justify-center mb-8">
                      {/* Profile image */}
                      <img 
                        src="/images/home/testimonial-jm.jpg" 
                        alt="Jessica Martinez" 
                        className="w-24 h-24 rounded-full shadow-lg object-cover"
                        loading="lazy"
                        width="96"
                        height="96"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-24 h-24 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full shadow-lg flex items-center justify-center text-white text-2xl font-bold" style={{display: 'none'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex justify-center mb-6">
                      <div className="flex text-yellow-400 space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                    <div className="text-center mb-6">
                      <span className="bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">Days saved weekly</span>
                    </div>
                    <p className="text-gray-600 italic text-center text-lg leading-relaxed">
                      "We cut our product categorization time from 2 weeks to just 6 hours. What used to be our biggest bottleneck is now completely automated with Categorix."
                    </p>
                    <div className="mt-8 text-center">
                      <p className="text-lg font-medium text-gray-900">Jessica Martinez</p>
                      <p className="text-md text-indigo-600">E-commerce Manager, Furnify Home</p>
                    </div>
                  </div>
                </div>
                
                {/* Testimonial 2 */}
                <div className={`transform transition-all duration-500 ease-in-out ${activeTestimonial === 1 ? 'opacity-100 z-10' : 'opacity-0 absolute inset-0 -z-10'}`}>
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 shadow-xl">
                    <div className="flex justify-center mb-8">
                      {/* Profile image */}
                      <img 
                        src="/images/home/testimonial-rk.jpg" 
                        alt="Robert Knight" 
                        className="w-24 h-24 rounded-full shadow-lg object-cover"
                        loading="lazy"
                        width="96"
                        height="96"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-24 h-24 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full shadow-lg flex items-center justify-center text-white text-2xl font-bold" style={{display: 'none'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex justify-center mb-6">
                      <div className="flex text-yellow-400 space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                    <div className="text-center mb-6">
                      <span className="bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">10,000+ products mapped in 1 day</span>
                    </div>
                    <p className="text-gray-600 italic text-center text-lg leading-relaxed">
                      "Mapped our entire catalog to 3 new marketplaces in a single day. Before Categorix, this would have required hiring 2 temporary staff for a month."
                    </p>
                    <div className="mt-8 text-center">
                      <p className="text-lg font-medium text-gray-900">Robert Knight</p>
                      <p className="text-md text-indigo-600">COO, Quantum Electronics</p>
                    </div>
                  </div>
                </div>
                
                {/* Testimonial 3 */}
                <div className={`transform transition-all duration-500 ease-in-out ${activeTestimonial === 2 ? 'opacity-100 z-10' : 'opacity-0 absolute inset-0 -z-10'}`}>
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 shadow-xl">
                    <div className="flex justify-center mb-8">
                      {/* Profile image */}
                      <img 
                        src="/images/home/testimonial-al.jpg" 
                        alt="Amelia Lewis" 
                        className="w-24 h-24 rounded-full shadow-lg object-cover"
                        loading="lazy"
                        width="96"
                        height="96"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-24 h-24 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full shadow-lg flex items-center justify-center text-white text-2xl font-bold" style={{display: 'none'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex justify-center mb-6">
                      <div className="flex text-yellow-400 space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                    <div className="text-center mb-6">
                      <span className="bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">40% conversion increase</span>
                    </div>
                    <p className="text-gray-600 italic text-center text-lg leading-relaxed">
                      "Not only did we save time, but our products are now properly categorized across all platforms. Sales increased 40% because customers can actually find our products."
                    </p>
                    <div className="mt-8 text-center">
                      <p className="text-lg font-medium text-gray-900">Amelia Lewis</p>
                      <p className="text-md text-indigo-600">Digital Marketing Director, Stylenova</p>
                    </div>
                  </div>
                </div>
                
                {/* Navigation Dots */}
                <div className="mt-8 flex justify-center space-x-2">
                  {[0, 1, 2].map((index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTestimonial(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${activeTestimonial === index ? 'bg-indigo-600 w-8' : 'bg-gray-300'}`}
                      aria-label={`View testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA section */}
        <div className="bg-gradient-to-r from-indigo-700 to-blue-700">
          <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              <span className="block">Ready to save hours on</span>
              <span className="block">product categorization?</span>
            </h2>
            <p className="mt-4 text-lg leading-7 text-blue-100">
              Join thousands of businesses automating their taxonomy mapping. Upload your first batch of products in minutes—no credit card required.
            </p>
            
            <div className="mt-10 flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
              {user ? (
                <Link
                  to="/dashboard"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-white hover:bg-blue-50 transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  Go to Dashboard
                </Link>
              ) : (
                <Link
                  to="/register"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-white hover:bg-gray-50 transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  Start Your Free Trial
                </Link>
              )}
              
              <Link
                to="/pricing"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-white text-base font-medium rounded-md text-white hover:bg-white/10 transform transition-all duration-300 hover:scale-105"
              >
                View Pricing Plans
              </Link>
            </div>
            
            <p className="text-blue-100 text-sm mt-4">
              30-Day Money-Back Guarantee • Free tier includes 20 products per month
            </p>
          </div>
        </div>
      </div>
      
      <Suspense fallback={<div>Loading...</div>}>
        <PublicFooter />
      </Suspense>
    </>
  );
};

export default Home;